import { canUseDOM } from '@ps/web-core-utils';
import classNames from 'clsx';
import { groupBy, isEmpty } from 'lodash';
import React, { useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';

import {
  CasinoGameIconSL,
  CasinoPlaySelectorSL,
  GameInfoIconSL,
  getIconClassKeys,
  useGameInfoEnabled
} from '@swf/casino';
import useTouchSupported from '@swf/core/hooks/useTouchSupported';
import { useWidget } from '@swf/core/hooks/useWidget';
import { select } from '@swf/core/store';
import type { TileType } from '@swf/core/types/CasinoGame';
import { IconTypes, MarketingTag, MosaicContext } from '@swf/core/types/CasinoGame';
import { isLiveCasinoGame } from '@swf/core/utils/casino';

import type { SLProps } from '../CasinoGameList/CasinoGameList';

import styles from './CasinoGameTileSL.scss';
import MarketingIcon from './components/MarketingIcon/MarketingIcon';
import Number from './components/Number/Number';
import Overlay from './components/Overlay/Overlay';
import useLaunchGame from './hooks/useLaunchGame';
import type { CasinoGameTileSLProps } from './types';

const CasinoGameTileSL: React.FC<CasinoGameTileSLProps> = ({
  className,
  count,
  fsbLaunch,
  game,
  icon,
  index,
  noIconScale,
  onLaunch,
  preload,
  mosaicHint,
  launchSource,
  gameCategory,
  numbered = false,
  overlayHierarchy,
  disableIcons,
  gameDeck,
  isMosaic
}) => {
  const hasInfoRoute = useSelector(select.casino.hasInfoRoute);
  const isMobile = useSelector(select.device.isMobile);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const isTouchSupported = useTouchSupported();
  const [isVisible, setVisible] = useState(false);
  const { widgetConfig } = useWidget();
  const launchGame = useLaunchGame({
    game,
    gameCategory,
    count,
    launchSource,
    fsbLaunch,
    onLaunch,
    index
  });
  const { tile_type: tileType = 'poster' as TileType } = widgetConfig as SLProps;
  const tileTypeToShow: TileType =
    gameDeck || isMosaic ? (icon === IconTypes.Wide ? 'wide' : 'poster') : tileType;
  const infoEnabled = useGameInfoEnabled();

  const { jackpot, name, fsb, tags = [] } = game;
  const tables = useMemo(() => groupBy(game.tables, 'playType'), [game.tables]);
  const showGameInfoIcon = game?.tags && game?.tags.includes(MarketingTag.GameInfo);
  const [isOverlay, setIsOverlay] = useState(false);

  const iconClass = getIconClassKeys(icon || 'icnSquare').map(
    (val: string) => styles[val as keyof typeof styles]
  );

  const { ref } = useInView({
    onChange: setVisible,
    triggerOnce: true,
    rootMargin: canUseDOM && !isMobile ? `0px 50px 50px 0px` : undefined
  });

  if (isEmpty(tables) && !isLiveCasinoGame(game)) return null;

  return (
    <div
      className={classNames(className, numbered && styles.numberedWrapper, styles.tileOuterWrapper)}
    >
      <div
        className={classNames(styles.tilesWrapper, iconClass, isTouchSupported && styles.touch, {
          [styles.overlayBorder]: isOverlay,
          [styles.mosaic]: mosaicHint === MosaicContext.BigIcon
        })}
      >
        {isOverlay && <div className={styles.overlayInner} />}
        <CasinoGameIconSL
          className={styles.tileHover}
          icon={icon}
          game={game}
          preload={preload}
          scale={!noIconScale}
          mosaicHint={mosaicHint}
          onClick={isTouchSupported ? () => launchGame() : undefined}
          visible={isVisible}
          innerRef={ref}
          tileType={tileTypeToShow}
        >
          {(preload || isVisible) && (
            <>
              <Overlay
                jackpot={jackpot}
                tags={game?.tags}
                key={name}
                tables={tables}
                visible={isVisible}
                setIsOverlay={setIsOverlay}
                overlayHierarchy={overlayHierarchy}
              />
              {isAuthenticated && !numbered && (
                <MarketingIcon fsb={fsb} disableIcons={disableIcons} tags={tags} styles={styles} />
              )}
              {hasInfoRoute && showGameInfoIcon && infoEnabled && (
                <GameInfoIconSL game={game} count={count} index={index} />
              )}
              {!isTouchSupported && (
                <CasinoPlaySelectorSL
                  className={styles.tileHover}
                  multiplayer={Object.keys(tables).length > 1}
                  onClick={launchGame}
                  size="small"
                  game={game}
                />
              )}
            </>
          )}
        </CasinoGameIconSL>
      </div>

      {numbered && typeof index !== 'undefined' && <Number index={index} />}
    </div>
  );
};

export default CasinoGameTileSL;
